import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, RefreshCw } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import {
  useTargetingBuilderDeviceTypesQuery,
  useTargetingBuildersQuery,
  useTargetingStatusCountsQuery,
} from '../../hooks';
import { resolveTargetingStatusTone, TargetingStatus, type TargetingBuilder } from '../../model';
import { useTargetingBuilderRouteFilters } from '../../useTargetingBuilderRouteFilters';
import { TargetingBuilderActions } from '../TargetingBuilderActions';
import { TargetingSummaryMenu } from '../TargetingSummaryMenu';

export function TargetingBuilderTable() {
  const { can } = useShigoPermissions();
  const canCreate = can(ShigoPermission.TargetingCreate);
  const canEdit = can(ShigoPermission.TargetingEdit);
  const { filters, queryFilter, setPagination, setSearch, setStatuses } =
    useTargetingBuilderRouteFilters();
  const query = useTargetingBuildersQuery(queryFilter);
  const counts = useTargetingStatusCountsQuery(queryFilter.searchTerm);
  const deviceTypesQuery = useTargetingBuilderDeviceTypesQuery();
  const deviceTypeNames = React.useMemo(
    () =>
      new Map((deviceTypesQuery.data ?? []).map((deviceType) => [deviceType.id, deviceType.name])),
    [deviceTypesQuery.data],
  );
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TargetingBuilder>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('targetingBuilder.name'),
          headerClassName: 'pl-4',
          className: 'pl-4 font-medium',
          cell: (targeting) =>
            canEdit ? (
              <Link
                className={dashboardTableTextActionClassName()}
                to="/targeting/$targetingId/edit"
                params={{ targetingId: targeting.id }}
              >
                {targeting.name}
              </Link>
            ) : (
              targeting.name
            ),
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('targetingBuilder.status'),
          label: (targeting) => dashboardT(`targetingBuilder.statuses.${targeting.status}`),
          tone: (targeting) => resolveTargetingStatusTone(targeting.status),
        },
        {
          id: 'geoLocations',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('targetingBuilder.geo'),
          cell: (targeting) => (
            <TargetingSummaryMenu
              title={dashboardT('targetingBuilder.geo')}
              groups={[
                {
                  label: dashboardT('targeting.included'),
                  items: targeting.geoLocations
                    .filter((location) => !location.excluded)
                    .map(resolveLocationLabel),
                },
                {
                  label: dashboardT('targeting.excluded'),
                  items: targeting.geoLocations
                    .filter((location) => location.excluded)
                    .map(resolveLocationLabel),
                },
              ]}
            />
          ),
        },
        {
          id: 'deviceTypes',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('targetingBuilder.devices'),
          cell: (targeting) => (
            <TargetingSummaryMenu
              title={dashboardT('targetingBuilder.devices')}
              items={targeting.deviceTypes.map(
                (deviceType) =>
                  deviceTypeNames.get(deviceType.deviceTypeId) ?? deviceType.deviceTypeId,
              )}
            />
          ),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canEdit,
          cell: (targeting) => <TargetingBuilderActions id={targeting.id} name={targeting.name} />,
        },
      ]),
    [canEdit, deviceTypeNames],
  );
  const selectedStatuses = new Set(filters.statuses);
  const statusLabel = filters.statuses.length
    ? filters.statuses.map((status) => dashboardT(`targetingBuilder.statuses.${status}`)).join(', ')
    : dashboardT('targetingBuilder.allStatuses');

  function updateStatus(status: TargetingStatus, checked: boolean) {
    const statuses = checked
      ? Array.from(new Set([...filters.statuses, status]))
      : filters.statuses.filter((selectedStatus) => selectedStatus !== status);
    setStatuses(statuses);
  }

  return (
    <DashboardDataTable
      title={dashboardT('targetingBuilder.title')}
      tableLabel={dashboardT('targetingBuilder.title')}
      columns={columns}
      items={query.data?.content ?? []}
      getRowKey={(targeting) => targeting.id}
      loading={query.isLoading}
      loadingMessage={dashboardT('targetingBuilder.loading')}
      emptyMessage={dashboardT('targetingBuilder.empty')}
      errorMessage={query.isError ? dashboardT('targetingBuilder.loadError') : undefined}
      minWidthClassName="min-w-[720px]"
      pagination={{
        value: {
          page: filters.page,
          pageSize: filters.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: [5, 10, 25, 50, 100],
        disabled: query.isLoading,
        onChange: (next) => {
          setPagination(next.page, next.pageSize);
        },
      }}
      toolbar={
        <>
          <TargetingBuilderSearchInput
            key={filters.searchTerm}
            initialValue={filters.searchTerm}
            onChange={setSearch}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <DashboardTableFilterTrigger
                label={statusLabel}
                aria-label={dashboardT('targetingBuilder.statusFilter')}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {Object.values(TargetingStatus).map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.has(status)}
                  onSelect={(event) => {
                    event.preventDefault();
                  }}
                  onCheckedChange={(checked) => {
                    updateStatus(status, checked);
                  }}
                >
                  <span className="flex min-w-0 flex-1 justify-between gap-3">
                    <span>{dashboardT(`targetingBuilder.statuses.${status}`)}</span>
                    {counts.data ? (
                      <span className="text-muted-foreground">{counts.data[status] ?? 0}</span>
                    ) : null}
                  </span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="icon"
            aria-label={dashboardT('targetingBuilder.refresh')}
            onClick={() => {
              void query.refetch();
              void counts.refetch();
            }}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </Button>
          {canCreate ? (
            <Button asChild>
              <Link to="/targeting/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('targetingBuilder.create')}
              </Link>
            </Button>
          ) : null}
        </>
      }
    />
  );
}

function resolveLocationLabel(location: TargetingBuilder['geoLocations'][number]) {
  return location.locationName ?? location.countryName ?? location.geocodeId ?? '-';
}

function TargetingBuilderSearchInput({
  initialValue,
  onChange,
}: {
  initialValue: string;
  onChange: (value: string) => void;
}) {
  const [value, setValue] = React.useState(initialValue);
  const changedByUser = React.useRef(false);

  React.useEffect(() => {
    if (!changedByUser.current) return;
    const timeoutId = window.setTimeout(() => {
      onChange(value);
    }, 650);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [onChange, value]);

  return (
    <DashboardTableSearchInput
      value={value}
      aria-label={dashboardT('targetingBuilder.search')}
      placeholder={dashboardT('targetingBuilder.search')}
      onChange={(event) => {
        changedByUser.current = true;
        setValue(event.target.value);
      }}
    />
  );
}
